const fa = (value: number) => value.toLocaleString('fa-IR');

const persianDay = (isoDay: string) => {
  const [year, month, day] = isoDay.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('fa-IR', { day: 'numeric', month: 'short' });
};

export interface TrendSeries {
  label: string;
  color: string;
  values: number[];
}

// نمودار میله‌ای چندسریه برای روند ۱۴ روزه — SVG سبک بدون وابستگی
export function TrendChart({ days, series }: { days: string[]; series: TrendSeries[] }) {
  if (!days.length) return <div className="chart-empty">داده‌ی روند هنوز در دسترس نیست؛ یک بار دیگر «به‌روزرسانی» را بزنید.</div>;
  const max = Math.max(1, ...series.flatMap((item) => item.values));
  const slot = 100 / days.length;
  const barWidth = slot / (series.length + 0.6);
  return <figure className="admin-chart trend-chart">
    <figcaption className="chart-legend">{series.map((item) => <span key={item.label}><i style={{ background: item.color }} />{item.label}</span>)}</figcaption>
    <svg viewBox="0 0 100 42" preserveAspectRatio="none" role="img" aria-label="روند ۱۴ روز اخیر">
      {[0.25, 0.5, 0.75, 1].map((ratio) => <line key={ratio} className="grid-line" x1="0" x2="100" y1={34 - 30 * ratio} y2={34 - 30 * ratio} />)}
      {days.map((day, dayIndex) => series.map((item, seriesIndex) => {
        const value = item.values[dayIndex] ?? 0;
        const height = (value / max) * 30;
        const x = 100 - (dayIndex + 1) * slot + (slot - barWidth * series.length) / 2 + seriesIndex * barWidth;
        return <rect key={`${day}-${item.label}`} x={x} y={34 - height} width={barWidth * 0.92} height={Math.max(value ? height : 0.4, 0)} rx="0.7" fill={item.color}><title>{`${persianDay(day)} — ${item.label}: ${fa(value)}`}</title></rect>;
      }))}
      <line x1="0" x2="100" y1="34" y2="34" className="axis-line" />
    </svg>
    <div className="chart-x-labels">
      <span>{persianDay(days[0])}</span>
      <span>{persianDay(days[Math.floor(days.length / 2)])}</span>
      <span>{persianDay(days[days.length - 1])}</span>
    </div>
  </figure>;
}

// توزیع امتیازها ۱ تا ۵
export function RatingBars({ distribution }: { distribution: Record<string, number> }) {
  const rows = [5, 4, 3, 2, 1].map((rating) => ({ rating, count: Number(distribution[String(rating)] ?? distribution[rating] ?? 0) }));
  const max = Math.max(1, ...rows.map((row) => row.count));
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  return <div className="admin-chart rating-bars">
    {rows.map((row) => <div key={row.rating} className="rating-row">
      <span className="rating-label">{fa(row.rating)} ★</span>
      <span className="rating-track"><i style={{ width: total ? `${(row.count / max) * 100}%` : '0%' }} /></span>
      <span className="rating-count">{fa(row.count)}</span>
    </div>)}
    <small className="rating-total">{total ? `از مجموع ${fa(total)} نظر ثبت‌شده` : 'هنوز نظری ثبت نشده است.'}</small>
  </div>;
}

// قیف تبدیل جلسه → تولید → اقدام سبد
export function FunnelChart({ stages }: { stages: Array<{ label: string; value: number; color: string }> }) {
  const top = Math.max(1, stages[0]?.value ?? 1);
  return <div className="admin-chart funnel-chart">
    {stages.map((stage, index) => {
      const ratio = (stage.value / top) * 100;
      const previous = index > 0 ? stages[index - 1] : null;
      const stepRate = previous && previous.value ? `(${fa(Math.round((stage.value / previous.value) * 100))}٪ از مرحله قبل)` : '';
      return <div key={stage.label} className="funnel-stage">
        <span className="funnel-meta"><b>{stage.label}</b><strong>{fa(stage.value)}</strong></span>
        <span className="funnel-track"><i style={{ width: `${ratio}%`, background: stage.color }} /></span>
        <small className="funnel-rate">{stepRate}</small>
      </div>;
    })}
    {stages[0]?.value > 0 && <small className="funnel-total">نرخ تبدیل نهایی: {fa(Math.round(((stages[stages.length - 1]?.value ?? 0) / stages[0].value) * 100))}٪</small>}
  </div>;
}
